import React from 'react'
import {
  MotifButton,
  MotifCard,
  MotifCardBody,
  MotifIcon,
  MotifFab,
  MotifToggleSwitch,
  MotifTooltip,
} from '@ey-xd/motif-react'
import {
  IconoirGoogleDocs,
  IconoirCheckCircle,
  IconoirInfoCircle,
  IconoirWarningTriangle,
  IconoirEditPencil,
} from '@ey-xd/motif-icon'
import moment from 'moment'
import { HRDocumentStatusType, HRListType, IndexingStepInfo } from './type'
import './List.scss'

export default function HRListItem({
  id,
  displayName,
  createdBy,
  createdDate,
  isActive,
  isIndexing = false,
  indexingStep,
  isArchived = false,
  handleToggleSwitch,
  showMapping,
  documentStatus
}: HRListType) {
  const toggleTooltipId = React.useId()
  const maliciousTooltipId = React.useId()
  const mappingTooltipId = React.useId()
  return (
    <MotifCard
      className="hr-list-item"
      orientation="horizontal"
      variant="card-border"
    >
      <MotifCardBody>
        <div className="list-image">
          <MotifFab aria-label="settings" type="button">
            <MotifIcon src={IconoirGoogleDocs} />
          </MotifFab>
        </div>
        <div className="list-content">
          <div className="motif-body2-default-regular"></div>
          <div
            className={`motif-body2-default-regular font-size-16 ${
              documentStatus === HRDocumentStatusType.ScanFailed
                ? 'text-grey'
                : 'text-link'
            }`}
          >
            {displayName}
          </div>
          <div className="motif-body2-default-regular">
            Uploaded by {createdBy}
          </div>
          <div className="motif-body2-default-regular">
            {moment(createdDate).format('MM/DD/YYYY')}
          </div>
          <div className="toggle-switch">
            <MotifToggleSwitch
              id={`toggle-${id}`}
              onLabel="Active"
              offLabel="Inactive"
              checked={isActive}
              onChange={() => {
                handleToggleSwitch(id, isActive)
              }}
              disabled={documentStatus !== HRDocumentStatusType.MappingCompleted || isArchived}
            />
            <span className="info-icon">
              <MotifTooltip
                allowClick
                allowHover
                hideCloseButton
                flip
                forceHideCounter={0}
                id={toggleTooltipId}
                offset={[0, 8]}
                placement="top"
                style={{ marginLeft: '10px' }}
                trigger={
                  <MotifIcon
                    src={IconoirInfoCircle}
                    fill="black"
                    stroke="black"
                  />
                }
                usePortal
                variant="default"
              >
                <div className="motif-body2-default-bold">
                  Active and Inactive
                </div>
                <div className="motif-caption-default-light motif-tooltip-custom-width">
                  {isArchived
                    ? 'This is an archived engagement and is now read-only.'
                    : documentStatus === HRDocumentStatusType.MappingCompleted
                      ? 'You can only activate one list at a time. The active list is selected for your SOD analysis. Always use the latest list for your analysis.'
                      : documentStatus === HRDocumentStatusType.ScanFailed
                        ? 'Malicious file detected — this document cannot be activated.'
                        : isProcessingStatus(documentStatus)
                          ? 'This document is still being processed and cannot be activated yet.'
                          : 'Please create a mapping for this document before it can be activated.'}
                </div>
              </MotifTooltip>
            </span>
          </div>
          <div className="mapping-button">
            {isIndexing ? null : documentStatus === HRDocumentStatusType.ScanFailed ? (
              <MotifTooltip
                allowClick
                allowHover
                hideCloseButton
                flip
                forceHideCounter={0}
                id={maliciousTooltipId}
                offset={[0, 8]}
                placement="top"
                trigger={
                  <MotifButton
                    size="small"
                    type="button"
                    variant="secondary"
                    disabled
                    className="icon-label-button"
                  >
                    <MotifIcon src={IconoirWarningTriangle} size="16" fill="#C71E1E" stroke="#C71E1E" />
                    Malware Detected
                  </MotifButton>
                }
                usePortal
                variant="default"
              >
                <div className="motif-body2-default-bold">Malicious File Detected</div>
                <div className="motif-caption-default-light motif-tooltip-custom-width">
                  File removed — a security threat was detected during scanning. We
                  recommend a virus scan on your device and notifying the file
                  provider.
                </div>
              </MotifTooltip>
            ) : isProcessingStatus(documentStatus) ? (
              <MotifButton size="small" type="submit" variant="primary" disabled>
                <span className="button-spinner" />
                {getButtonLabel(documentStatus)}
              </MotifButton>
            ) : (
              <MotifTooltip
                allowClick
                allowHover
                hideCloseButton
                flip
                forceHideCounter={0}
                id={mappingTooltipId}
                offset={[0, 8]}
                placement="top"
                trigger={
                  documentStatus === HRDocumentStatusType.MappingCompleted ? (
                    <MotifButton size="small" type="submit" onClick={() => showMapping(id)} variant="secondary" className="icon-label-button" disabled={isArchived}>
                      <MotifIcon src={IconoirCheckCircle} size="16" />
                      Edit Mapping
                    </MotifButton>
                  ) : (
                    <MotifButton size="small" type="submit" onClick={() => showMapping(id)} variant="primary" className="icon-label-button" disabled={isArchived}>
                      <MotifIcon src={IconoirEditPencil} size="16" />
                      Create Mapping
                    </MotifButton>
                  )
                }
                usePortal
                variant="default"
              >
                <div className="motif-caption-default-light motif-tooltip-custom-width">
                  {isArchived
                    ? 'This is an archived engagement and is now read-only.'
                    : documentStatus === HRDocumentStatusType.MappingCompleted
                      ? 'Mapping and indexing complete. Further changes are supported and will trigger reindexing.'
                      : 'Ready for data mapping'}
                </div>
              </MotifTooltip>
            )}
            <div className="status-message-group">
              {isIndexing && (
                <IndexingStepTracker step={indexingStep} />
              )}
            </div>
          </div>
        </div>
      </MotifCardBody>
    </MotifCard>
  )
}

function isProcessingStatus(documentStatus: number): boolean {
  return (
    documentStatus === HRDocumentStatusType.Uploading ||
    documentStatus === HRDocumentStatusType.Temporary ||
    documentStatus === HRDocumentStatusType.MalwareScan
  )
}

function getButtonLabel(documentStatus: number): string {
  if (documentStatus === HRDocumentStatusType.Uploading) return 'Uploading...'
  if (documentStatus === HRDocumentStatusType.Temporary) return 'Processing...'
  if (documentStatus === HRDocumentStatusType.MalwareScan) return 'Scanning for malware...'
  return 'Click here to Map'
}

const STEP_LABELS = [
  'Starting HR processing',
  'Extracting HR rows',
  'Indexing for AI Search',
  'Indexing completed',
  'Syncing to database',
  'All steps completed',
]

function IndexingStepTracker({ step }: { step?: IndexingStepInfo }) {
  const currentStep = step?.step ?? 0
  const description = step?.description ?? 'Embedding in progress'

  return (
    <div className="indexing-step-tracker">
      <div className="indexing-step-header">
        <span className="indexing-loader" />
        <span className="indexing-step-description">{description}</span>
      </div>
      <div className="indexing-step-bar">
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1
          const isDone = currentStep > stepNum
          const isActive = currentStep === stepNum
          return (
            <div
              key={stepNum}
              className={`indexing-step-dot ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}
              title={label}
            />
          )
        })}
      </div>
      <div className="indexing-step-count">
        Step {currentStep || '...'} of 6
      </div>
    </div>
  )
}